import { useState, type ReactNode } from "react";
import clsx from "clsx";

import { hashSeed, seededRange, variationFor } from "../lib/seed";
import { Tape } from "./Tape";

export type PhotoMountProps = {
  /** The entry id. Every bit of physical variation is derived from it. */
  seedId: string;
  src: string | null;
  alt: string;
  className?: string;
  /** Tailwind aspect class for the print, e.g. "aspect-4/3". */
  aspect?: string;
  fit?: "cover" | "contain";
  /** The print is still in the developer: desaturated, low contrast, bathed. */
  developing?: boolean;
  /** Play the one-time reveal as the photograph resolves to full colour. */
  revealing?: boolean;
  /** The photograph exists but couldn't be identified — hold it more gently. */
  muted?: boolean;
  /** Overlay content: plate numbers, stamps, progress. */
  children?: ReactNode;
  /** Presigned GET URLs last about an hour; offer a way to fetch a fresh one. */
  onImageError?: () => void;
  /** Above-the-fold photographs shouldn't be lazy. */
  eager?: boolean;
};

/**
 * The mount: a photographic print resting on the card, held down by glassine
 * photo corners, gummed tape, or paste, with a hair of rotation and a soft
 * realistic shadow.
 *
 * Which of those it gets, and how far it turns, comes from a hash of the entry
 * id — so a find looks the same on every reload, and the feed still doesn't
 * look like a spreadsheet.
 */
export function PhotoMount({
  seedId,
  src,
  alt,
  className,
  aspect = "aspect-4/3",
  fit = "cover",
  developing = false,
  revealing = false,
  muted = false,
  children,
  onImageError,
  eager = false,
}: PhotoMountProps) {
  const [failed, setFailed] = useState(false);
  const variation = variationFor(seedId);

  const showTape = variation.mount === "tape";
  const showCorners = variation.mount === "corners";

  return (
    <div
      className={clsx("relative", className)}
      style={{
        transform: `rotate(${variation.rotation}deg) translate(${variation.offsetX}px, ${variation.offsetY}px)`,
      }}
    >
      {/* The print itself: a white border, as though trimmed by a machine. */}
      <div
        className={clsx(
          "relative bg-card shadow-mount",
          // Pasted-in prints were trimmed with a wider margin of card.
          variation.mount === "paste" ? "p-[5px]" : "p-[3px]",
        )}
      >
        <div className={clsx("relative overflow-hidden bg-paper-deep", aspect)}>
          {src && !failed ? (
            <img
              src={src}
              alt={alt}
              loading={eager ? "eager" : "lazy"}
              decoding="async"
              onError={() => setFailed(true)}
              className={clsx(
                "h-full w-full transition-[filter,opacity] duration-700",
                fit === "cover" ? "object-cover" : "object-contain",
                developing && "scale-[1.01] opacity-55 saturate-[0.12] contrast-[0.85] blur-[2px]",
                revealing && "animate-develop",
                muted && !developing && "saturate-[0.55] opacity-90",
              )}
            />
          ) : (
            <div className="grain absolute inset-0 flex flex-col items-center justify-center gap-2 bg-paper-deep px-4 text-center">
              {failed ? (
                <>
                  <p className="field-label-tight text-ink-soft">Plate unavailable</p>
                  <p className="max-w-[22ch] font-prose text-xs leading-snug text-ink-soft">
                    The link to this photograph has expired.
                  </p>
                  {onImageError ? (
                    <button
                      type="button"
                      onClick={() => {
                        setFailed(false);
                        onImageError();
                      }}
                      className="link font-label text-xs"
                    >
                      Fetch it again
                    </button>
                  ) : null}
                </>
              ) : (
                <p className="field-label-tight text-ink-soft opacity-70">No plate mounted</p>
              )}
            </div>
          )}

          {/* The chemical sheen of an undeveloped print. */}
          {developing ? (
            <div aria-hidden="true" className="developing absolute inset-0" />
          ) : null}

          {children}
        </div>
      </div>

      {showCorners ? (
        <>
          <span className="corner-tab -top-px -left-px" />
          <span className="corner-tab -top-px -right-px rotate-90" />
          <span className="corner-tab -right-px -bottom-px rotate-180" />
          <span className="corner-tab -bottom-px -left-px -rotate-90" />
        </>
      ) : null}

      {showTape ? <TapeSet seedId={seedId} placement={variation.tape} /> : null}
    </div>
  );
}

function TapeSet({ seedId, placement }: { seedId: string; placement: string }) {
  const a = seededRange(hashSeed(seedId, "tape-a"), -6, 6, 1);
  const b = seededRange(hashSeed(seedId, "tape-b"), -6, 6, 1);

  switch (placement) {
    case "top":
      return <Tape length="long" rotation={a} className="-top-2.5 left-1/2 -translate-x-1/2" />;
    case "top-left":
      return <Tape rotation={a - 42} className="-top-3 -left-4" />;
    case "top-right":
      return <Tape rotation={b + 42} className="-top-3 -right-4" />;
    case "corners":
    default:
      return (
        <>
          <Tape rotation={a - 42} className="-top-3 -left-4" />
          <Tape rotation={b + 42} className="-right-4 -bottom-3" />
        </>
      );
  }
}
